import { Divider } from "@mui/material";
import { BedIcon, CalendarBlankIcon, UserIcon } from "@phosphor-icons/react";
import dayjs from "dayjs";
import { type ReactNode } from "react";
import { useTranslations } from "use-intl";

import type { FindUserReservationsExternalHotelReservation } from "@/app/lib/types/codegen";
import { LazyImage } from "@/app/ui/LazyImage";
import { boardIcons } from "@/features/hotels/components/hotels/BoardIcons";
import { HotelName } from "@/features/hotels/components/hotels/HotelName";
import { HotelStars } from "@/features/hotels/components/hotels/HotelStars";

export const BookedHotelCard = ({
  onClick,
  reservation,
  footer,
}: {
  reservation: FindUserReservationsExternalHotelReservation;
  onClick?: () => void;
  footer?: ReactNode;
}) => {
  const t = useTranslations();

  // show hotel check-in and check-out dates
  const startDate = reservation.serviceDates.startDate.split("T")[0];
  const endDate = reservation.serviceDates.endDate.split("T")[0];
  const { hotel } = reservation;

  const children = reservation.travelers.filter(
    (traveler) => traveler.type === "child",
  );

  const adults = reservation.travelers.filter(
    (traveler) => traveler.type === "adult",
  );

  const boardBasis = Array.from(
    new Set(reservation.rooms.map(({ boardBasis }) => boardBasis).flat()),
  );

  return (
    <button
      type="button"
      className="section-card w-full text-start"
      onClick={onClick}
    >
      <div className="flex w-full justify-between gap-4">
        <div className="flex min-h-[130px] w-[90px] shrink-0 items-center overflow-clip rounded-2xl bg-gray-100 text-center text-xs text-text-tertiary">
          {hotel.mainImageUrl ? (
            <LazyImage
              className="h-full w-full object-cover"
              sentryElementClassName="self-start"
              src={hotel.mainImageUrl}
              alt={hotel.name}
            />
          ) : (
            <span className="grow">{t("noImage")}</span>
          )}
        </div>

        <div className="flex w-full flex-col">
          <span className="mb-1 line-clamp-2 text-base/snug font-semibold text-text-title">
            <HotelName name={hotel.name} />
          </span>

          {!!hotel.stars && <HotelStars stars={hotel.stars} />}

          <ul className="mt-2 flex flex-col gap-2">
            {boardBasis.map((bb) => (
              <li key={bb} className="flex items-center">
                <div className="flex items-center gap-2 text-xs">
                  {boardIcons[bb]}
                  {t(`hotels.boardBasis.${bb}`)}
                </div>
              </li>
            ))}

            <li className="flex items-center justify-start gap-2 text-xs">
              <BedIcon size={16} />
              <span>
                {t("reservations.hotelRooms", {
                  count: reservation.rooms.length,
                })}
              </span>
            </li>

            <li className="flex items-center justify-start gap-2 text-xs">
              <CalendarBlankIcon size={16} />
              <span>
                {dayjs(startDate).format("ddd, DD MMMM")} -{" "}
                {dayjs(endDate).format("ddd, DD MMMM")}
              </span>
            </li>

            <li className="flex items-center justify-start gap-2 text-xs">
              <UserIcon size={16} />
              <span>
                {children.length > 0 ? (
                  <>{t("reservations.children", { count: children.length })} </>
                ) : null}
                {t("reservations.adults", { count: adults.length })}
              </span>
            </li>
          </ul>
        </div>
      </div>

      {footer ? (
        <>
          <Divider
            sx={{
              borderColor: "var(--color-stroke-neutral-softer)",
              my: "calc(var(--spacing)*4)",
            }}
          />
          {footer}
        </>
      ) : null}
    </button>
  );
};
